import { useRef, useEffect, useCallback } from 'react';

/**
 * Hook to manage auto-save initialization state for form components.
 * 
 * This hook encapsulates a common pattern used in auto-save forms where we need to:
 * 1. Track whether the form has been initialized with server data
 * 2. Reset initialization state when dependencies change (e.g., appointmentId)
 * 3. Mark the form as initialized after data is loaded (with a deferred tick)
 * 
 * @param resetDeps - Dependencies that should trigger a reset of initialization state
 * @returns Object with isInitialized ref and markInitialized function
 * 
 * @example
 * const { isInitialized, markInitialized } = useAutoSaveInit([appointmentId]);
 * 
 * useEffect(() => {
 *   if (data && !isInitialized.current) {
 *     // Initialize form state from data
 *     setFormValue(data.value);
 *     markInitialized();
 *   }
 * }, [data]);
 * 
 * useEffect(() => {
 *   // Skip auto-save until initialized
 *   if (!isInitialized.current) return;
 *   // Auto-save logic...
 * }, [debouncedValue]);
 */
export const useAutoSaveInit = (resetDeps: unknown[]) => {
  const isInitialized = useRef(false);

  // Reset initialization when dependencies change
  useEffect(() => {
    isInitialized.current = false;
  }, resetDeps);

  // Mark as initialized with a deferred tick to prevent immediate auto-save trigger
  const markInitialized = useCallback(() => {
    setTimeout(() => {
      isInitialized.current = true;
    }, 0);
  }, []);

  return { isInitialized, markInitialized };
};



